import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft, Loader2, RefreshCw, CheckCircle, XCircle, 
  Clock, Server, FolderSync, FileText, AlertTriangle,
  Building, Calendar
} from "lucide-react";
import { API } from "@/App";

export const SyncPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statusRes, historyRes] = await Promise.all([
        axios.get(`${API}/sync/sftp-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/sync/history?limit=10`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setStatus(statusRes.data);
      setHistory(historyRes.data.history || []);
    } catch (error) {
      console.error("Error fetching sync data:", error);
      toast.error("Failed to load sync status");
    } finally {
      setLoading(false);
    }
  };

  const runSyncNow = async () => {
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/sync/run-now`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Sync completed: ${response.data.properties_added} added, ${response.data.properties_updated} updated`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate("/admin")}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-semibold font-outfit">10/90 Data Sync</h1>
                <p className="text-sm text-muted-foreground">Automatic property & compliance sync</p>
              </div>
            </div>
            <Button onClick={runSyncNow} disabled={syncing || !status?.connection_ok}>
              {syncing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <RefreshCw className="w-4 h-4 mr-2" />
              )}
              Sync Now
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Connection Status */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Server className="w-5 h-5" />
              SFTP Connection
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  {status?.connection_ok ? (
                    <Badge className="bg-green-100 text-green-700">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Connected
                    </Badge>
                  ) : (
                    <Badge className="bg-red-100 text-red-700">
                      <XCircle className="w-3 h-3 mr-1" />
                      Disconnected
                    </Badge>
                  )}
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Host</span>
                  <span className="font-mono text-sm">{status?.host || "Not configured"}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Port</span>
                  <span className="font-mono text-sm">{status?.port}</span>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Files Waiting</span>
                  <Badge variant="outline">
                    <FileText className="w-3 h-3 mr-1" />
                    {status?.files_found?.length || 0} files
                  </Badge>
                </div>
                {status?.files_found?.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-2">Files ready to sync:</p>
                    {status.files_found.map((file, i) => (
                      <p key={i} className="text-sm font-mono">{file}</p>
                    ))}
                  </div>
                )}
                {status?.error && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                    <p className="text-sm text-red-700">{status.error}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Last Sync Info */}
        {status?.last_sync && (
          <Card className="border-green-200 bg-green-50/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <FolderSync className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-medium">Last Successful Sync</p>
                    <p className="text-sm text-muted-foreground">
                      {formatDate(status.last_sync.synced_at)}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-green-700">
                    {status.last_sync.properties_added + status.last_sync.properties_updated}
                  </p>
                  <p className="text-sm text-muted-foreground">properties synced</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Instructions for 10/90 */}
        <Card className="border-blue-200 bg-blue-50/50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <AlertTriangle className="w-5 h-5" />
              Setup Instructions for 10/90
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-blue-800">
              Provide these SFTP credentials to 10/90 so they can push property data:
            </p>
            <div className="bg-white rounded-lg p-4 border border-blue-200 font-mono text-sm space-y-2">
              <p><strong>Host:</strong> {status?.host}</p>
              <p><strong>Port:</strong> {status?.port}</p>
              <p><strong>Username:</strong> u530640</p>
              <p><strong>Password:</strong> [The password you set]</p>
              <p><strong>Directory:</strong> / (root)</p>
            </div>
            <p className="text-sm text-blue-800">
              Ask 10/90 to push a property export file (Excel/CSV) to this SFTP. 
              The sync will automatically pick it up and import the data.
            </p>
          </CardContent>
        </Card>

        {/* Sync History */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5" />
              Sync History
            </CardTitle>
          </CardHeader>
          <CardContent>
            {history.length === 0 ? (
              <div className="text-center py-8">
                <FolderSync className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-muted-foreground">No sync history yet</p>
                <p className="text-sm text-muted-foreground">
                  Syncs will appear here once 10/90 pushes files to the SFTP
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {history.map((sync) => (
                  <div 
                    key={sync.sync_id} 
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {sync.status === "completed" ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                      ) : sync.status === "completed_with_errors" ? (
                        <AlertTriangle className="w-5 h-5 text-amber-500" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-500" />
                      )}
                      <div>
                        <p className="font-medium text-sm">
                          {formatDate(sync.synced_at)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {sync.files_processed?.length || 0} files processed
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="flex gap-2">
                        <Badge variant="outline" className="bg-green-50">
                          <Building className="w-3 h-3 mr-1" />
                          +{sync.properties_added}
                        </Badge>
                        <Badge variant="outline" className="bg-blue-50">
                          <RefreshCw className="w-3 h-3 mr-1" />
                          {sync.properties_updated}
                        </Badge>
                      </div>
                      {sync.errors?.length > 0 && (
                        <p className="text-xs text-red-500 mt-1">
                          {sync.errors.length} errors
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};
